/**
 * The built-in default workspace layout (ADR-024 §3; ADR-033 §3.1).
 *
 * # Part 2 (architect 2026-06-08): named presets removed
 *
 * The named-preset machinery (Single Map / Map + Platforms / Operations
 * Center / Situational Awareness / Display-only as *user-pickable* shapes)
 * was removed — layouts are captured live on save into a Profile, not chosen
 * from a seeded list. What survives is a **single canonical default/fallback
 * layout**: the former Situational-Awareness three-pane shape, re-exported
 * here as :data:`DEFAULT_LAYOUT`. It is the workspace's fallback when a saved
 * ``dockview_state`` is absent / empty / corrupt / role-inaccessible
 * (REQ-UI-054 / 047) and the shape the built-in default Profile opens on
 * (REQ-UI-077). There is no longer a ``preset_name`` lookup, a ``PRESETS``
 * map, or a ``resolvePresetLayout`` resolver.
 *
 * Sizes use proportional units (raw pixels); dockview-react normalises them at
 * apply-time so the actual rendered widths follow the proportional ratios at
 * the user's viewport size.
 *
 * Cross-references:
 *   - ADR-024 §3 (layout sizing intent).
 *   - REQ-UI-054 / 047 (the default is the schema-mismatch + role-filter
 *     fallback the workspace applies).
 *   - REQ-UI-077 (the built-in default Profile opens on this shape).
 *   - ADR-033 §3.1 (Profile document carries the layout).
 */
import type { Orientation, SerializedDockview } from "dockview-react";
import type { PanelId } from "@/multi-pane/registry-types";
import { PANEL_TITLES } from "@/multi-pane/registry-types";

/**
 * Dockview-react v5's ``Orientation`` enum values are plain
 * string literals (``"HORIZONTAL"``, ``"VERTICAL"``). We import
 * the enum as a TYPE only — referencing the runtime value
 * triggers the dockview-react package's CSS-in-JS injection at
 * module-load time, which collides with Radix UI's portal-id
 * generation in jsdom (Radix IDs like ``:r1:`` aren't escaped in
 * jsdom-internal querySelector calls, producing malformed
 * selectors that the strict CSSOM rejects). String literals
 * decouple this module from the runtime side-effect so tests that
 * transitively import it (e.g., preferences-menu.test) don't fail
 * to render.
 *
 * The workspace component (:file:`./multi-pane-workspace.tsx`)
 * keeps the runtime import — it needs the actual dockview
 * package — and is exercised via Playwright (real Chrome) rather
 * than vitest, so the jsdom incompatibility doesn't bite.
 */
// Cast string literals to the imported ``Orientation`` type
// (a string-valued enum from dockview-react v5 whose values are
// the literal strings "HORIZONTAL" / "VERTICAL"). The
// ``as Orientation`` is sound because TypeScript represents
// string enums as their literal values at runtime.
const HORIZONTAL = "HORIZONTAL" as Orientation;

/**
 * Build the ``panels`` map entry for a single panel id. The
 * ``contentComponent`` field looks up
 * :data:`panelComponents` (see :file:`./panel-registry.tsx`);
 * the ``title`` populates the dockview tab header.
 */
function panelEntry(id: PanelId) {
  return { id, contentComponent: id, title: PANEL_TITLES[id] };
}

/**
 * The built-in default layout — the standard operator picture, and the shape
 * the built-in default Profile opens on (REQ-UI-077). Formerly the
 * "Situational Awareness" preset; now the single canonical default/fallback.
 *
 * A single-level horizontal split into three groups per ADR-024 §3:
 *
 *   ``[ Map (+ Query · System Status · Audit Log) | Platforms·Detections·Alerts | Entity Details ]``
 *
 * - A **left tab group** (~57%) hosting the **Map** plus **Query** (every
 *   authenticated role — ADR-038 §Decision 5 / REQ-UI-111, positioned
 *   immediately right of Map and left of the two gated surfaces) and the
 *   role-gated **System Status** (support_engineer / admin) and **Audit Log**
 *   (admin) surfaces as tabs, with **Map the active tab**. For operator /
 *   analyst sessions the two gated tabs are surgically elided at restore
 *   (:func:`filterLayoutByRole` in ``./layout-role-filter.ts``) — those roles
 *   land on Map + Query and the group reflows; support_engineer / admin see
 *   the extra tab(s) one click from the map (architect 2026-06-10). Layouts
 *   saved before Query existed receive it in the same position at restore via
 *   :func:`insertQueryPanel` (ADR-038 D15 / REQ-UI-112).
 * - A **middle tab group** (~21%) hosting Platforms, Detections, and
 *   Alerts as three tabs in that left-to-right order, with **Platforms
 *   the active/selected tab** (operators land on the platform roster;
 *   Detections / Alerts are one click away). dockview models a tab
 *   group as a single ``leaf`` whose ``views`` array carries every
 *   tab and whose ``activeView`` names the selected one.
 * - **Entity Details** rightmost (~22%) — the selection-driven detail
 *   pane (kept narrow; it's an adjunct surface per ADR-024 §2).
 *
 * Sizes 729 / 269 / 282 ≈ 57 / 21 / 22 of a 1280 canvas (proportional;
 * dockview-react normalises them at apply-time so the rendered widths follow
 * the ratio at the user's viewport). The middle group is **25% narrower** than
 * its former 358 (architect 2026-06-10), the freed width given to the map.
 *
 * Unlike the pre-2026-06-10 default, this layout carries **role-restricted
 * panels** (System Status, Audit Log), so it is no longer true that every panel
 * here is operator-accessible. The workspace's restore-time
 * :func:`filterLayoutByRole` (REQ-UI-047 / ADR-024 §6) elides the inaccessible
 * tabs per session role and reflows the grid — an operator's *effective*
 * default is the Map + middle + Entity Details three-pane shape, while admins
 * get the fuller picture. (This is the surgical elision ADR-024 §6 always
 * specified; the earlier all-or-nothing gate that forced an
 * all-operator-accessible default was retired in the same change.)
 */
export const DEFAULT_LAYOUT: SerializedDockview = {
  grid: {
    root: {
      type: "branch",
      data: [
        {
          // The left group: the Map plus the role-gated System Status
          // (support_engineer / admin) + Audit Log (admin) surfaces as tabs.
          // For an operator / analyst session the latter two are surgically
          // elided at restore (:func:`filterLayoutByRole`), leaving Map alone —
          // REQ-UI-047 / ADR-024 §6 (architect 2026-06-10).
          type: "leaf",
          data: {
            views: ["map", "query", "system-status", "audit-log"],
            activeView: "map",
            id: "group-sa-map",
          },
          size: 729,
        },
        {
          // The middle tab group: one leaf, four views. ``activeView``
          // selects Platforms; the ``views`` order is the left-to-right
          // tab order (Platforms · Detections · Tracks · Alerts — Tracks
          // joined after Detections per ADR-041 D-5 / REQ-UI-179).
          type: "leaf",
          data: {
            views: ["platforms", "detections", "tracks", "alerts"],
            activeView: "platforms",
            id: "group-sa-center",
          },
          size: 269,
        },
        {
          type: "leaf",
          data: {
            views: ["entity-details"],
            activeView: "entity-details",
            id: "group-sa-details",
          },
          size: 282,
        },
      ],
    },
    width: 1280,
    height: 800,
    orientation: HORIZONTAL,
  },
  panels: {
    map: panelEntry("map"),
    query: panelEntry("query"),
    "system-status": panelEntry("system-status"),
    "audit-log": panelEntry("audit-log"),
    platforms: panelEntry("platforms"),
    detections: panelEntry("detections"),
    tracks: panelEntry("tracks"),
    alerts: panelEntry("alerts"),
    "entity-details": panelEntry("entity-details"),
  },
  activeGroup: "group-sa-center",
};
